import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Collapse,
  IconButton,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Schedule,
  AccountBalance,
  TrendingUp,
  Info,
  AttachMoney,
  Warning,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminBankingManagement from '../components/AdminBankingManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Job {
  serviceRequestId: string;
  bidId: string;
  paymentId: string;
  serviceDate?: string | null;
  paymentDate?: string | null;
  completedDate: string | null;
  jobAmount: number;
  serviceAmount?: number;
  salesTaxAmount?: number;
  processingFeeAmount?: number;
  providerCut: number;
  platformCut: number;
  platformFeePercentage?: number;
  feePercentage: number;
  customerName?: string;
  serviceType?: string;
  address?: string;
  stripeSettlementStatus?: string;
  stripeSettledDate?: string;
  stripeBatchNumber?: string;
}

interface PayoutSummary {
  provider: {
    id: string;
    name: string;
    businessName: string;
    email: string;
  };
  weekStartDate: string;
  weekEndDate: string;
  jobs: Job[];
  summary: {
    totalJobs: number;
    totalJobAmount: number;
    totalProviderCut: number;
    totalPlatformCut: number;
    totalServiceAmount?: number;
    totalProcessingFee?: number;
    totalSalesTax?: number;
    averageFeePercentage: number;
  };
  status: string;
  isFirstPayout: boolean;
}

interface PayoutData {
  weekStartDate: string;
  weekEndDate: string;
  payouts: PayoutSummary[];
  grandTotal: {
    providers: number;
    totalJobs: number;
    totalJobAmount: number;
    totalProviderCut: number;
    totalPlatformCut: number;
  };
  holdPeriod: string;
  note: string;
  settlementReconcile?: { updated: number };
}

const PayoutsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payoutData, setPayoutData] = useState<PayoutData | null>(null);
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [forcePayoutDialog, setForcePayoutDialog] = useState<{
    open: boolean;
    provider: PayoutSummary | null;
    notes: string;
    loading: boolean;
  }>({
    open: false,
    provider: null,
    notes: '',
    loading: false,
  });
  const [forcePayoutEmpty, setForcePayoutEmpty] = useState<{
    open: boolean;
    loading: boolean;
    fetching: boolean;
    providers: Array<{ id: string; name: string; businessName: string; email: string; jobCount: number; totalProviderCut: number; totalJobAmount: number }>;
    debug?: { skipped?: Array<{ id: string; name: string; reason: string }> };
    selectedId: string;
    notes: string;
  }>({
    open: false,
    loading: false,
    fetching: false,
    providers: [],
    selectedId: '',
    notes: '',
  });
  const [seasonalBackfillLoading, setSeasonalBackfillLoading] = useState(false);
  const [seasonalBackfillConfirmOpen, setSeasonalBackfillConfirmOpen] = useState(false);

  const runSeasonalBackfill = async () => {
    try {
      setSeasonalBackfillLoading(true);
      setSeasonalBackfillConfirmOpen(false);
      setError(null);
      const res = await axios.post(
        `${API_URL}/admin/subscriptions/backfill-seasonal-payments`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = res?.data?.data ?? {};
      const created = data.created ?? 0;
      const existed = data.alreadyExisted ?? 0;
      const skipped = data.skipped ?? 0;
      const errCount = Array.isArray(data.errors) ? data.errors.length : 0;
      setSuccess(
        `Seasonal backfill done: ${created} created, ${existed} already on file, ${skipped} skipped` +
          (errCount ? `, ${errCount} issue(s) — check server logs if needed` : '') +
          '. Refresh Weekly Payouts; new rows need the normal settle/hold window.'
      );
      await fetchPayouts();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Seasonal backfill failed');
    } finally {
      setSeasonalBackfillLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/payouts/current-week`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.success) {
        setPayoutData(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching payouts:', err);
      setError(err.response?.data?.message || 'Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandProvider = (providerId: string) => {
    setExpandedProvider(expandedProvider === providerId ? null : providerId);
  };

  const handleProcessPayouts = async () => {
    if (!payoutData) return;

    try {
      setProcessing(true);
      setError(null);

      const payouts = payoutData.payouts.map((payout) => ({
        providerId: payout.provider.id,
        weekStartDate: payout.weekStartDate,
        weekEndDate: payout.weekEndDate,
        jobs: payout.jobs.map((job) => ({
          serviceRequestId: job.serviceRequestId,
          bidId: job.bidId,
          paymentId: job.paymentId,
          completedDate: job.completedDate,
          jobAmount: job.jobAmount,
          providerCut: job.providerCut,
          platformCut: job.platformCut,
          feePercentage: job.feePercentage,
        })),
        totalJobAmount: payout.summary.totalJobAmount,
        totalProviderCut: payout.summary.totalProviderCut,
        totalPlatformCut: payout.summary.totalPlatformCut,
        notes,
      }));

      const response = await axios.post(
        `${API_URL}/payouts/mark-processed`,
        { payouts },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        setSuccess('Payouts processed! Stripe Connect transfers have been sent to providers.');
        setProcessDialogOpen(false);
        setNotes('');
        // Refresh data
        fetchPayouts();
      }
    } catch (err: any) {
      console.error('Error processing payouts:', err);
      setError(err.response?.data?.message || 'Failed to process payouts');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading payout data...</Typography>
      </Container>
    );
  }

  if (!payoutData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="info">
          <AlertTitle>No Payouts Ready</AlertTitle>
          There are no payouts ready to process this week. This could mean:
          <ul>
            <li>Completed charges are still inside the hold window (first payout: 7 days; all: 3 days after settled date)</li>
            <li>All settled, unpaid jobs have already been paid out</li>
            <li>Provider Analytics &quot;completed&quot; only means charged — Weekly Payouts also needs LawnHub settlement + W‑9</li>
          </ul>
        </Alert>
      </Container>
    );
  }

  const hasPayouts = payoutData.payouts.length > 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Payout Management
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Manage weekly payouts and provider banking information
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            label="Weekly Payouts" 
            icon={<AttachMoney />} 
            iconPosition="start"
          />
          <Tab 
            label="Banking Management" 
            icon={<AccountBalance />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Seasonal monthly payments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If Stripe already charged seasonal contracts but they never showed on Weekly Payouts, run this once.
            It creates missing LawnHub payment records from paid Stripe invoices (safe to re-run — skips duplicates).
          </Typography>
          <Button
            variant="outlined"
            disabled={seasonalBackfillLoading}
            onClick={() => setSeasonalBackfillConfirmOpen(true)}
          >
            {seasonalBackfillLoading ? 'Backfilling…' : 'Backfill seasonal payments'}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={seasonalBackfillConfirmOpen} onClose={() => !seasonalBackfillLoading && setSeasonalBackfillConfirmOpen(false)}>
        <DialogTitle>Backfill seasonal payments?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This scans all seasonal Stripe subscriptions, finds paid invoices, and creates any missing provider payment records.
            It may take a minute. Safe to run more than once.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeasonalBackfillConfirmOpen(false)} disabled={seasonalBackfillLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={runSeasonalBackfill} disabled={seasonalBackfillLoading}>
            {seasonalBackfillLoading ? 'Working…' : 'Run backfill'}
          </Button>
        </DialogActions>
      </Dialog>

      {!hasPayouts && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>No Payouts Ready</AlertTitle>
            There are no payouts ready to process this week. This could mean:
            <ul>
              <li>Completed charges are still inside the hold window (first payout: 7 days; all: 3 days after settled date)</li>
              <li>All settled, unpaid jobs have already been paid out</li>
              <li>Provider Analytics &quot;completed&quot; only means charged — check the Payout column there</li>
            </ul>
            {(payoutData.settlementReconcile?.updated ?? 0) > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Just reconciled {payoutData.settlementReconcile!.updated} completed payment(s) to settled — refresh if the list still looks empty.
              </Typography>
            )}
          </Alert>
          <Card sx={{ maxWidth: 560 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" />
                Force Payout (Override 7-day hold)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Process a payout immediately for a provider with unpaid, settled payments, bypassing the normal 7-day hold. Emergency use only.
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Warning />}
                onClick={async () => {
                  setForcePayoutEmpty(prev => ({ ...prev, open: true, fetching: true, providers: [], debug: undefined, selectedId: '', notes: '' }));
                  try {
                    const res = await axios.get(`${API_URL}/payouts/force-payout-eligible?debug=1`, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    });
                    const data = res?.data?.data ?? {};
                    const list = (data.providers ?? []) as Array<{ id: string; name: string; businessName: string; email: string; jobCount: number; totalProviderCut: number; totalJobAmount: number }>;
                    const debug = data.debug as { skipped?: Array<{ id: string; name: string; reason: string }> } | undefined;
                    setForcePayoutEmpty(prev => ({ ...prev, fetching: false, providers: list, debug, selectedId: list[0]?.id ?? '' }));
                  } catch (e: any) {
                    setError(e?.response?.data?.error || 'Failed to load eligible providers');
                    setForcePayoutEmpty(prev => ({ ...prev, fetching: false, open: false }));
                  }
                }}
              >
                Force Payout
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {hasPayouts && (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Week of {formatDate(payoutData.weekStartDate)} - {formatDate(payoutData.weekEndDate)}
            </Typography>
            <Alert severity="success" icon={<Info />} sx={{ mt: 2 }}>
              <AlertTitle>Smart Payout System: {payoutData.holdPeriod}</AlertTitle>
              {payoutData.note}
              {(payoutData.settlementReconcile?.updated ?? 0) > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Reconciled {payoutData.settlementReconcile!.updated} completed payment(s) that were missing a settlement flag — they may now appear below if hold rules pass.
                </Typography>
              )}
            </Alert>
          </Box>

      {/* Grand Total Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalance color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Total Charged
                </Typography>
              </Box>
              <Typography variant="h5">
                {formatCurrency(payoutData.grandTotal.totalJobAmount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {payoutData.grandTotal.totalJobs} jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountBalance color="warning" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Provider Payouts
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {formatCurrency(payoutData.grandTotal.totalProviderCut)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {payoutData.grandTotal.providers} providers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Platform Fee
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {formatCurrency(payoutData.grandTotal.totalPlatformCut)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of service price (not card total)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                startIcon={<CheckCircle />}
                onClick={() => setProcessDialogOpen(true)}
                disabled={payoutData.payouts.length === 0}
              >
                Process All Payouts
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Transfers to provider Stripe Connect accounts when you confirm
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Provider-by-Provider Breakdown */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Provider Breakdown
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Jobs</TableCell>
                <TableCell align="right">Total Charged</TableCell>
                <TableCell align="right">Provider Cut</TableCell>
                <TableCell align="right">Platform Fee</TableCell>
                <TableCell align="right">Avg Platform %</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payoutData.payouts.map((payout) => (
                <React.Fragment key={payout.provider.id}>
                  <TableRow hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {payout.provider.businessName || payout.provider.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {payout.provider.email}
                          </Typography>
                        </Box>
                        {payout.isFirstPayout && (
                          <Chip
                            label="1st Payout"
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={payout.summary.totalJobs}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(payout.summary.totalJobAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" color="warning.main">
                        {formatCurrency(payout.summary.totalProviderCut)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {formatCurrency(payout.summary.totalPlatformCut)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${payout.summary.averageFeePercentage}%`}
                        size="small"
                        color={payout.summary.averageFeePercentage > 12 ? 'success' : 'info'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Tooltip title="Force Payout (Emergency Only)">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<Warning />}
                            onClick={() => setForcePayoutDialog({ 
                              open: true, 
                              provider: payout, 
                              notes: '',
                              loading: false 
                            })}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Force Payout
                          </Button>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandProvider(payout.provider.id)}
                        >
                          {expandedProvider === payout.provider.id ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Job Details */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                      <Collapse
                        in={expandedProvider === payout.provider.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            Job Details
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <Tooltip title="Service date = day the work was for. Payment date = when the customer card was charged.">
                                    <span>Service / Payment</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Service</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Total charged to the customer's card (service + sales tax + payment processing).">
                                    <span>Charged</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Provider share of service price after platform fee, plus sales tax (provider remits tax). Not always 99% — depends on customer fee tier (often 1%, 5%, etc.).">
                                    <span>Provider Cut</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="LawnHub platform fee = platform % of service price only (not of tax or processing).">
                                    <span>Platform Fee</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Stripe card processing gross-up (2.9% + $0.30 style) so service + tax clear after Stripe fees.">
                                    <span>Processing</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Sales tax charged to the customer and included in the provider cut (provider remits).">
                                    <span>Tax</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center">Settlement</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {payout.jobs.map((job, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Typography variant="caption" display="block">
                                      Svc: {job.serviceDate ? formatDate(job.serviceDate) : (job.completedDate ? formatDate(job.completedDate) : '—')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Pay: {job.paymentDate ? formatDate(job.paymentDate) : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {job.customerName || 'Unknown'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {job.serviceType || 'Service'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                      {job.address || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(job.jobAmount)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" color="warning.main">
                                      {formatCurrency(job.providerCut)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" color="success.main" component="span">
                                      {formatCurrency(job.platformCut)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {(job.platformFeePercentage ?? job.feePercentage)}% of service
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">
                                      {formatCurrency(job.processingFeeAmount || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">
                                      {formatCurrency(job.salesTaxAmount || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip title={`Settled: ${job.stripeSettledDate ? formatDate(job.stripeSettledDate) : 'N/A'}${job.stripeBatchNumber ? ` | Batch: ${job.stripeBatchNumber}` : ''}`}>
                                      <Chip
                                        label={job.stripeSettlementStatus || 'settled'}
                                        size="small"
                                        color={job.stripeSettlementStatus === 'settled' ? 'success' : 'default'}
                                      />
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell colSpan={4} align="right">
                                  <Typography variant="body2" fontWeight="bold">
                                    TOTAL:
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCurrency(payout.summary.totalJobAmount)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                                    {formatCurrency(payout.summary.totalProviderCut)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold" color="success.main">
                                    {formatCurrency(payout.summary.totalPlatformCut)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCurrency(payout.summary.totalProcessingFee || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCurrency(payout.summary.totalSalesTax || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell />
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Process Payouts Dialog */}
      <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Weekly Payouts</DialogTitle>
        <DialogContent>
          <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
            <AlertTitle>What happens when you confirm</AlertTitle>
            1. Review all payout amounts above.<br />
            2. Click &quot;Confirm&quot; to process. Stripe Connect transfers will be sent immediately to each provider&apos;s connected account.<br />
            3. Providers receive funds in their Stripe Express balance; bank deposits follow their Stripe payout schedule (e.g. weekly on Fridays).
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes about this payout batch..."
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Summary:</strong>
            </Typography>
            <Typography variant="body2">
              • {payoutData.grandTotal.providers} providers
            </Typography>
            <Typography variant="body2">
              • Total to pay out: <strong>{formatCurrency(payoutData.grandTotal.totalProviderCut)}</strong>
            </Typography>
            <Typography variant="body2">
              • Your platform earnings: <strong>{formatCurrency(payoutData.grandTotal.totalPlatformCut)}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleProcessPayouts}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {processing ? 'Processing...' : 'Confirm & Mark Processed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Payout Dialog */}
      <Dialog 
        open={forcePayoutDialog.open} 
        onClose={() => setForcePayoutDialog({ open: false, provider: null, notes: '', loading: false })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Force Payout (Emergency Only)
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Emergency Use Only</AlertTitle>
            This will immediately process a payout for{' '}
            <strong>{forcePayoutDialog.provider?.provider.businessName || forcePayoutDialog.provider?.provider.name}</strong>{' '}
            bypassing normal hold periods. Use only in emergencies.
          </Alert>

          {forcePayoutDialog.provider && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Payout Summary:</strong>
              </Typography>
              <Typography variant="body2">
                • Jobs: {forcePayoutDialog.provider.summary.totalJobs || 0}
              </Typography>
              <Typography variant="body2">
                • Provider Cut: <strong>{formatCurrency(forcePayoutDialog.provider.summary.totalProviderCut || 0)}</strong>
              </Typography>
              {(forcePayoutDialog.provider.summary as any).totalFines && (forcePayoutDialog.provider.summary as any).totalFines > 0 && (
                <Typography variant="body2" color="error">
                  • Fines: <strong>-{formatCurrency((forcePayoutDialog.provider.summary as any).totalFines)}</strong>
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1 }}>
                • Net Amount: <strong>{formatCurrency(
                  (forcePayoutDialog.provider.summary.totalProviderCut || 0) - 
                  ((forcePayoutDialog.provider.summary as any).totalFines || 0)
                )}</strong>
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Emergency Notes (Optional)"
            value={forcePayoutDialog.notes}
            onChange={(e) => setForcePayoutDialog(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Reason for emergency payout..."
            sx={{ mb: 2 }}
          />

          <Alert severity="info" icon={<Info />}>
            This payout will be processed immediately via Stripe Connect and cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setForcePayoutDialog({ open: false, provider: null, notes: '', loading: false })}
            disabled={forcePayoutDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              if (!forcePayoutDialog.provider) return;

              try {
                setForcePayoutDialog(prev => ({ ...prev, loading: true }));
                setError(null);

                const response = await axios.post(
                  `${API_URL}/payouts/force-payout/${forcePayoutDialog.provider.provider.id}`,
                  { notes: forcePayoutDialog.notes || 'Emergency payout initiated by admin' },
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                  }
                );

                if (response.data.success) {
                  setSuccess(`Emergency payout processed! ${response.data.message}`);
                  setForcePayoutDialog({ open: false, provider: null, notes: '', loading: false });
                  fetchPayouts(); // Refresh data
                } else {
                  throw new Error(response.data.error || 'Failed to process payout');
                }
              } catch (err: any) {
                console.error('Error forcing payout:', err);
                setError(err.response?.data?.message || 'Failed to process emergency payout');
                setForcePayoutDialog(prev => ({ ...prev, loading: false }));
              }
            }}
            disabled={forcePayoutDialog.loading}
            startIcon={forcePayoutDialog.loading ? <CircularProgress size={20} /> : <Warning />}
          >
            {forcePayoutDialog.loading ? 'Processing...' : 'Confirm Force Payout'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {/* Force Payout (no payouts) – select provider, then confirm */}
      <Dialog
        open={forcePayoutEmpty.open}
        onClose={() => setForcePayoutEmpty(prev => ({ ...prev, open: false, loading: false, fetching: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Force Payout (Override 7-day hold)
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Emergency use only</AlertTitle>
            This processes a payout immediately, bypassing the 7-day hold. Use only when necessary.
          </Alert>
          {forcePayoutEmpty.fetching ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading providers with unpaid settled payments…</Typography>
            </Box>
          ) : forcePayoutEmpty.providers.length === 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No providers have unpaid, settled payments eligible for force payout.
              </Typography>
              {forcePayoutEmpty.debug?.skipped?.length ? (
                <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Why providers were skipped:</strong>
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                    {forcePayoutEmpty.debug.skipped.map((s) => (
                      <li key={s.id}>{s.name || s.id}: {s.reason}</li>
                    ))}
                  </Box>
                </Typography>
              ) : null}
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={forcePayoutEmpty.selectedId}
                  label="Provider"
                  onChange={(e) => setForcePayoutEmpty(prev => ({ ...prev, selectedId: e.target.value }))}
                >
                  {forcePayoutEmpty.providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.businessName || p.name || p.email} – {formatCurrency(p.totalProviderCut)} ({p.jobCount} job{p.jobCount !== 1 ? 's' : ''})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes (optional)"
                value={forcePayoutEmpty.notes}
                onChange={(e) => setForcePayoutEmpty(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Reason for emergency payout…"
                sx={{ mb: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setForcePayoutEmpty(prev => ({ ...prev, open: false, loading: false, fetching: false }))}
            disabled={forcePayoutEmpty.loading}
          >
            Cancel
          </Button>
          {forcePayoutEmpty.providers.length > 0 && (
            <Button
              variant="contained"
              color="warning"
              disabled={forcePayoutEmpty.loading || !forcePayoutEmpty.selectedId}
              startIcon={forcePayoutEmpty.loading ? <CircularProgress size={20} /> : <Warning />}
              onClick={async () => {
                if (!forcePayoutEmpty.selectedId) return;
                setForcePayoutEmpty(prev => ({ ...prev, loading: true }));
                setError(null);
                try {
                  const res = await axios.post(
                    `${API_URL}/payouts/force-payout/${forcePayoutEmpty.selectedId}`,
                    { notes: forcePayoutEmpty.notes || 'Emergency payout (override 7-day hold)' },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                  );
                  if (res?.data?.success) {
                    setSuccess(res?.data?.message ?? 'Force payout processed.');
                    setForcePayoutEmpty(prev => ({ ...prev, open: false, loading: false, fetching: false }));
                    fetchPayouts();
                  } else {
                    throw new Error(res?.data?.error ?? 'Force payout failed');
                  }
                } catch (e: any) {
                  setError(e?.response?.data?.error ?? e?.message ?? 'Force payout failed');
                  setForcePayoutEmpty(prev => ({ ...prev, loading: false }));
                }
              }}
            >
              {forcePayoutEmpty.loading ? 'Processing…' : 'Confirm Force Payout'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </Box>
      )}

      {activeTab === 1 && (
        <AdminBankingManagement />
      )}
    </Container>
  );
};

export default PayoutsPage;

